import { create } from "zustand";
import axios from "axios";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";

interface IStore {
  uploadedImage: string | null;
  thumbnailImage: string | null;
  uploadImage: (image: File, includeThumbnail?: boolean) => void;
  /** Uploads and returns the path without touching uploadedImage, for a
   *  second image on the same screen (e.g. a challenge's card image). */
  uploadFile: (image: File) => Promise<string | null>;
  setUploadedImage: (image: string | null) => void;
  setUploadedThumbnail: (image: string) => void;
  clearUploadedImage: () => void;
}

// Gives each uploadFile call its own axios duplicate-guard key.
let directUploadSeq = 0;

const postImage = async (
  image: File,
  includeThumbnail: boolean,
  params?: Record<string, string>
) => {
  const body = new FormData();
  body.append("file", image);
  body.append("type", "dashboard");
  body.append("includeThumbnail", includeThumbnail ? "true" : "false");
  // In development REACT_APP_UPLOAD_URL points to the staging backend so
  // uploads work without a local storage service. In production the env
  // var is unset and the relative path resolves via the axios baseURL.
  const uploadUrl = process.env.REACT_APP_UPLOAD_URL ?? "storage/upload";
  const response = await axios.post(uploadUrl, body, {
    headers: { "Content-Type": "multipart/form-data" },
    params,
  });
  return response?.data;
};

export const useUploadImage = create<IStore>()((set) => ({
  /** State */
  uploadedImage: null,
  thumbnailImage: null,
  /** Callbacks */
  uploadImage: async (image, includeThumbnail = false) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const data = await postImage(image, includeThumbnail);
      if (data?.filePath) {
        set({
          uploadedImage: data?.filePath,
          thumbnailImage: data?.thumbnailPath ?? null,
        });
      }
    } catch (error) {
      LOG.error("Error on uploading image", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  uploadFile: async (image) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      // Its own params, so the axios duplicate guard (method, url, params and
      // data, and FormData always serialises to {}) cancels neither this call
      // nor one that overlaps it, from uploadImage or from another uploadFile.
      const data = await postImage(image, false, {
        target: "direct",
        seq: String(++directUploadSeq),
      });
      return data?.filePath ?? null;
    } catch (error) {
      LOG.error("Error on uploading image", { error });
      return null;
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  setUploadedImage: (image) => {
    set({ uploadedImage: image });
  },
  setUploadedThumbnail: (thumbImage) => {
    set({ thumbnailImage: thumbImage });
  },
  clearUploadedImage: () => {
    set({ uploadedImage: null, thumbnailImage: null });
  },
}));
